import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Info, Loader2, Save, Search, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';

const money = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);

export default function ProductCosts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [missingOnly, setMissingOnly] = useState(false);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category !== 'all') params.category = category;
      if (missingOnly) params.missing_only = true;
      setData(await adminService.getProductCosts(params));
      setEdits({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load product costs');
    } finally {
      setLoading(false);
    }
  }, [search, category, missingOnly]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const dirtyCount = useMemo(() => Object.keys(edits).length, [edits]);

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      const costs = Object.entries(edits).map(([product_id, value]) => ({
        product_id,
        unit_cost: value === '' ? null : Number(value),
      }));
      const result = await adminService.saveProductCosts(costs);
      toast.success(`${result.updated} cost${result.updated === 1 ? '' : 's'} saved${result.cleared ? `, ${result.cleared} cleared` : ''}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save costs');
    } finally {
      setSaving(false);
    }
  };

  const coverage = data?.coverage || {};
  const coveragePct = coverage.products
    ? Math.round((coverage.with_cost / coverage.products) * 100)
    : 0;

  return (
    <div className="space-y-5" data-testid="product-costs-page">
      <div className="flex items-start gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/reports/client-performance')}
          data-testid="back-to-performance-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-[240px]">
          <h2 className="text-2xl font-bold text-gray-900">Product Unit Costs</h2>
          <p className="text-sm text-gray-500 mt-1">
            What it costs you to make one unit. Fill the catalogue once and every margin, waste and
            profitability figure comes alive.
          </p>
        </div>
        <Button
          className="bg-amber-500 hover:bg-amber-600 min-h-[44px]"
          onClick={save}
          disabled={saving || !dirtyCount}
          data-testid="save-costs-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save'}
        </Button>
      </div>

      <Card className="border-amber-200 bg-amber-50/60">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900 leading-relaxed">
            Costs are stamped onto history as it is written, so changing a cost here never rewrites
            past months. Months with no cost on record show units and revenue with margin marked
            &ldquo;cost not recorded&rdquo; rather than a misleading zero.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="h-4 w-4 text-amber-500" /> Coverage
            <Badge variant="outline" className="ml-1" data-testid="cost-coverage">
              {coverage.with_cost || 0} of {coverage.products || 0} products ({coveragePct}%)
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="h-2 bg-gray-100 rounded overflow-hidden">
            <div className="h-full bg-amber-400 transition-all" style={{ width: `${coveragePct}%` }} />
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500">Search</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 text-gray-400 absolute left-2 top-2.5" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Product name"
                  className="h-9 pl-8"
                  data-testid="cost-search"
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs text-gray-500">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 mt-1" data-testid="cost-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {(data?.categories || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch
                checked={missingOnly}
                onCheckedChange={setMissingOnly}
                id="missing-only"
                data-testid="cost-missing-only"
              />
              <Label htmlFor="missing-only" className="text-sm">Missing cost only</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]" data-testid="product-costs-table">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-left">Category</th>
                    <th className="p-2 text-right">Wholesale price</th>
                    <th className="p-2 text-right w-36">Unit cost ($)</th>
                    <th className="p-2 text-right">Margin at list</th>
                    <th className="p-2 text-left">Last set</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-500">No products match.</td></tr>
                  )}
                  {(data?.items || []).map((p) => {
                    const edited = edits[p.product_id];
                    const value = edited !== undefined
                      ? edited
                      : (p.unit_cost === null || p.unit_cost === undefined ? '' : p.unit_cost);
                    const cost = value === '' ? null : Number(value);
                    const marginPct = cost !== null && p.wholesale_price
                      ? Math.round(((p.wholesale_price - cost) / p.wholesale_price) * 100)
                      : null;
                    return (
                      <tr key={p.product_id} className="border-t" data-testid={`cost-row-${p.product_id}`}>
                        <td className="p-2">
                          <span className="font-medium text-gray-900">{p.name}</span>
                          {p.status !== 'available' && (
                            <Badge variant="outline" className="ml-2 text-[10px] text-gray-500">
                              {p.status}
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 text-gray-500">{p.category}</td>
                        <td className="p-2 text-right">{money(p.wholesale_price)}</td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={value}
                            onChange={(e) => setEdits((prev) => ({
                              ...prev, [p.product_id]: e.target.value,
                            }))}
                            placeholder="—"
                            className={`h-9 text-right ${edited !== undefined ? 'border-amber-400 bg-amber-50' : ''}`}
                            data-testid={`cost-input-${p.product_id}`}
                          />
                        </td>
                        <td className="p-2 text-right">
                          {marginPct === null ? (
                            <span className="text-gray-400">cost not recorded</span>
                          ) : (
                            <span className={marginPct < 15 ? 'text-rose-600' : 'text-emerald-700'}>
                              {marginPct}%
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-gray-500">
                          {p.unit_cost_updated_at
                            ? `${new Date(p.unit_cost_updated_at).toLocaleDateString()}${p.unit_cost_updated_by ? ` · ${p.unit_cost_updated_by}` : ''}${p.unit_cost_source === 'recipe' ? ' · from recipe' : ''}`
                            : 'never'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
